// 主訴・暫定診断のステータス遷移を表現する純粋関数群（Vue非依存・単体テスト可能）。
// 「AI提案表示中は store answer は空」「confirmed/revised/saved のときだけ値をコミット」
// というルールをここに閉じ込める。

import type {
  AssessmentAnswer,
  AssessmentStatus,
  ComplaintState,
  ConsultationAssessmentDraft,
  DiagnosisCandidate,
  DiagnosisState,
  DiagnosisValue,
  PreprocessingStatus,
} from "./types";

// === 完了ステータス定義 ===

// 完了扱いのステータス。これらのときだけ store answer に値をコミットする。
export const DONE_STATUSES: readonly AssessmentStatus[] = ["confirmed", "revised", "saved"];

// 完了判定
export function isDone(status: AssessmentStatus): boolean {
  return DONE_STATUSES.includes(status);
}

// === 共通ユーティリティ ===
function isBlank(value: unknown): boolean {
  return value == null || String(value).trim() === "";
}

function hasDiagnosisValue(value: DiagnosisValue): boolean {
  return !!value.branch_id || !isBlank(value.original_disease);
}

function answerHasDiagnosisValue(answer: AssessmentAnswer | null | undefined): boolean {
  return !!answer?.branch_id || !isBlank(answer?.original_disease);
}

function isDiagnosisMatchingSuggestion(state: DiagnosisState): boolean {
  const currentBranchId = state.value.branch_id != null ? String(state.value.branch_id) : "";
  const suggestedBranchId = state.suggestedBranchId != null ? String(state.suggestedBranchId) : "";
  const currentName = (state.value.icd10BranchName || "").trim();
  const suggestedName = (state.suggestedName || "").trim();
  return currentBranchId === suggestedBranchId && currentName === suggestedName;
}

// === 主訴 ====

// AIが提案した「主訴」を返す。
function draftComplaintText(
  draft: ConsultationAssessmentDraft | null | undefined
): string | null {
  const raw = draft?.complaint;
  return typeof raw === "string" && raw.trim() !== "" ? raw : null;
}

// バックエンドに保存されているデータ(AnytimeConsultationAnswer)を受け取り、ComplaintStateを初期化する。
// AnytimeConsultationAnswerの主訴が空でなければ saved、空なら parsing で初期化する。
export function initComplaintState(answer: AssessmentAnswer | null | undefined): ComplaintState {
  const complaint = answer?.complaint ?? "";
  if (!isBlank(complaint)) {
    return { status: "saved", value: String(complaint), suggestedText: null };
  }
  return { status: "parsing", value: "", suggestedText: null };
}

// LLMの出力(ConsultationAssessmentDraft)を元に、ComplaintStateを作成する。
export function resolveComplaintFromPreprocessing(
  state: ComplaintState,
  preStatus: PreprocessingStatus,
  draft: ConsultationAssessmentDraft | null | undefined
): ComplaintState {
  if (isDone(state.status)) return state;

  if (preStatus === "completed") {
    const draftText = draftComplaintText(draft);
    if (draftText) {
      return { status: "suggested", value: draftText, suggestedText: draftText };
    }
    return { status: "empty", value: "", suggestedText: null };
  }
  if (preStatus === "failed") {
    return {
      status: isBlank(state.value) ? "empty" : "revised",
      value: state.value,
      suggestedText: null,
    };
  }
  return { ...state, status: "parsing" };
}

// 手入力・手動編集の評価
export function evaluateComplaintStatus(state: ComplaintState, nextValue: string): ComplaintState {
  const next: ComplaintState = { ...state, value: nextValue };
  const blank = isBlank(nextValue);
  const s = state.status;

  if (s === "parsing") {
    // 解析中でも手入力を許可し、値が入ったら手動編集扱い（revised）にする。
    if (!blank) next.status = "revised";
    return next;
  }
  if (blank) {
    next.status = "empty";
    return next;
  }
  if (s === "saved") {
    // 保存済みは手入力で編集しても saved を維持する（値が非空の限り）。
    return next;
  }
  if (
    (s === "suggested" || s === "confirmed") &&
    nextValue.trim() !== (state.suggestedText || "").trim()
  ) {
    next.status = "revised";
    return next;
  }
  if (s === "empty") {
    next.status = "revised";
  }
  return next;
}

// 「採用」ボタン を押した時の値に変換する
export function confirmComplaint(state: ComplaintState): ComplaintState {
  if (state.status === "suggested") return { ...state, status: "confirmed" };
  return state;
}

// store answer へのコミット値に変換する（完了時=値、非完了時=空）
export function complaintCommitValue(state: ComplaintState): string {
  return isDone(state.status) ? state.value : "";
}


// === 暫定診断 ===

// 空のDiagnosisValueを返す。branch_id と original_disease が null の状態。
export function emptyDiagnosisValue(): DiagnosisValue {
  return {
    branch_id: null,
    icd10BranchName: null,
    original_disease: null,
    icd10OriginalDisease: null,
  };
}

// AIが提案した「暫定診断」の第1候補を返す。候補がなければ null。
function draftFirstCandidate(
  draft: ConsultationAssessmentDraft | null | undefined
): DiagnosisCandidate | null {
  const candidates = draft?.icd10BranchCandidates ?? [];
  return candidates[0] ?? null;
}

// バックエンドに保存されているデータ(AnytimeConsultationAnswer)を受け取り、DiagnosisStateを初期化する。
// AnytimeConsultationAnswerの暫定診断が空でなければ saved、空なら parsing で初期化する。
export function initDiagnosisState(answer: AssessmentAnswer | null | undefined): DiagnosisState {
  if (answerHasDiagnosisValue(answer)) {
    return {
      status: "saved",
      value: {
        branch_id: answer?.branch_id ?? null,
        icd10BranchName: answer?.icd10BranchName ?? null,
        original_disease: answer?.original_disease ?? null,
        icd10OriginalDisease: answer?.icd10OriginalDisease ?? null,
      },
      suggestedBranchId: null,
      suggestedName: null,
    };
  }
  return {
    status: "parsing",
    value: emptyDiagnosisValue(),
    suggestedBranchId: null,
    suggestedName: null,
  };
}

// LLMの出力(ConsultationAssessmentDraft)を元に、ComplaintStateを作成する。
export function resolveDiagnosisFromPreprocessing(
  state: DiagnosisState,
  preStatus: PreprocessingStatus,
  draft: ConsultationAssessmentDraft | null | undefined
): DiagnosisState {
  if (isDone(state.status)) return state;

  if (preStatus === "completed") {
    const candidate = draftFirstCandidate(draft);
    if (candidate) {
      return {
        status: "suggested",
        value: {
          branch_id: candidate.id,
          icd10BranchName: candidate.name,
          original_disease: null,
          icd10OriginalDisease: null,
        },
        suggestedBranchId: candidate.id,
        suggestedName: candidate.name,
      };
    }
    return {
      status: "empty",
      value: emptyDiagnosisValue(),
      suggestedBranchId: null,
      suggestedName: null,
    };
  }
  if (preStatus === "failed") {
    return { ...state, status: hasDiagnosisValue(state.value) ? "revised" : "empty" };
  }
  return { ...state, status: "parsing" };
}

// 手入力・手動編集の評価
export function evaluateDiagnosisStatus(
  state: DiagnosisState,
  nextValue: DiagnosisValue
): DiagnosisState {
  const next: DiagnosisState = { ...state, value: nextValue };
  const has = hasDiagnosisValue(nextValue);
  const s = state.status;

  if (s === "parsing") {
    if (has) next.status = "revised";
  } else if (!has) {
    next.status = "empty";
  } else if (s === "saved") {
    // 保存済みは第1候補以外に変更しても saved を維持する。
  } else if (s === "empty") {
    next.status = "revised";
  } else if ((s === "suggested" || s === "confirmed") && !isDiagnosisMatchingSuggestion(next)) {
    next.status = "revised";
  }
  return next;
}

// 「採用」ボタン を押した時の値に変換する
export function confirmDiagnosis(state: DiagnosisState): DiagnosisState {
  if (state.status === "suggested") return { ...state, status: "confirmed" };
  return state;
}

// store answer へのコミット値に変換する（完了時=値、非完了時=空）
export function diagnosisCommitValue(state: DiagnosisState): DiagnosisValue {
  return isDone(state.status) ? { ...state.value } : emptyDiagnosisValue();
}
